import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";

import { colorModeContext, useMode } from "./theme";

import Sidebar from "./scenes/global/Sidebar";
import Topbar from "./scenes/global/Topbar";

import Dashboard from "./scenes/dashboard";
import ChatPage from "./scenes/Chat";
import KeysPage from "./scenes/Keys";
import OrganizationPage from "./scenes/Organization";
import ProvidersPage from "./scenes/providers";

import LoginPage from "./scenes/Login";
import { AccountPortalLogin, AccountPortalResults } from "./scenes/AccountPortal";
import WelcomeComplete from "./scenes/WelcomeComplete";
import RegisterPage from "./scenes/Register";
import PrivateRoute from "./components/PrivateRoute";
import Files from "./scenes/Files";
import LabResults from "./scenes/lab-results";
import Intake from "./scenes/intake";
import DepartmentResults from "./scenes/department-results";
import Patients from "./scenes/patients";
import Facilities from "./scenes/facilities";
import Reports from "./scenes/reports";
import Education from "./scenes/education";
import AnalytesPage from "./scenes/toxcutoffs";
import BarcodeGenerator from "./scenes/barcode-generator";
import Tracking from "./scenes/tracking";

// ✅ Admin pages
import Admin from "./scenes/admin";
import AdminOrganizations from "./scenes/admin/Organizations";
import AdminReportSettings from "./scenes/admin/ReportSettings";
import AdminDepartments from "./scenes/admin/Departments";
import AdminUsers from "./scenes/admin/Users";
import AuditLogs from "./scenes/admin/AuditLogs";
import Accounts from "./scenes/admin/Accounts";
import { ACTIVE_DEPARTMENT_EVENT, getActiveDepartment } from "./lib/departmentSelection";

function DepartmentAwareLabResults() {
  const [department, setDepartment] = useState(getActiveDepartment());

  useEffect(() => {
    const onChange = (event) => setDepartment(event.detail || getActiveDepartment());
    window.addEventListener(ACTIVE_DEPARTMENT_EVENT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(ACTIVE_DEPARTMENT_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  if (department?.id && String(department.code || "").toUpperCase() !== "TOX") {
    return <DepartmentResults />;
  }

  return <LabResults />;
}

function ProtectedLayout() {
  return (
    <div className="app">
      <Sidebar />
      <main className="content">
        <Topbar />
        <Outlet />
      </main>
    </div>
  );
}

export default function App() {
  const [theme, colorMode] = useMode();

  return (
    <colorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />

        <Routes>
          {/* Public */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/portal/login" element={<AccountPortalLogin />} />
          <Route path="/portal/results" element={<AccountPortalResults />} />
          <Route path="/welcome/complete" element={<WelcomeComplete />} />
          <Route path="/register" element={<RegisterPage />} />

          {/* Protected */}
          <Route
            element={
              <PrivateRoute>
                <ProtectedLayout />
              </PrivateRoute>
            }
          >
            {/* index → "/" */}
            <Route index element={<Dashboard />} />
            {/* also /dashboard */}
            <Route path="dashboard" element={<Dashboard />} />

            {/* Other protected pages */}
            <Route path="chat" element={<ChatPage />} />
            <Route path="keys" element={<KeysPage />} />
            <Route path="organization" element={<OrganizationPage />} />
            <Route path="files" element={<Files />} />

            <Route path="/lab-results" element={<DepartmentAwareLabResults />} />
            <Route path="/intake" element={<Intake view="waiting-list" />} />
            <Route path="/new-test-order" element={<Intake view="new-order" />} />
            <Route path="/department-results" element={<Navigate to="/lab-results" replace />} />
            <Route path="/patients" element={<Patients />} />
            <Route path="/barcode-generator" element={<BarcodeGenerator />} />
            <Route path="/tracking" element={<Tracking />} />
            <Route path="/providers" element={<ProvidersPage />} />
            <Route path="/facilities" element={<Facilities />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/education" element={<Education />} />
            <Route path="/education/:articleId" element={<Education />} />

            {/* ✅ Admin */}
            <Route path="/admin" element={<Admin />} />
            <Route path="/admin/organizations" element={<AdminOrganizations />} />
            <Route path="/admin/report-settings" element={<AdminReportSettings />} />
            <Route path="/admin/departments" element={<AdminDepartments />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/accounts" element={<Accounts />} />
            <Route path="/users" element={<Navigate to="/admin/users" replace />} />
            <Route path="/admin/audit-logs" element={<AuditLogs />} />
            <Route path="/analytes" element={<AnalytesPage />} />
            <Route path="/toxcutoffs" element={<Navigate to="/analytes" replace />} />

            {/* Catch-all: send anything else back to Dashboard */}
            <Route path="*" element={<Navigate to="" replace />} />
          </Route>
        </Routes>
      </ThemeProvider>
    </colorModeContext.Provider>
  );
}